<template>
    <Spinner v-if="displaySpinner" />
    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-book">Term</Eyebrow>
        </template>
        <template #title>
            <EditableTitle v-model="domainTerm" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="domainTerm?.identity"
                :entityId="domainTerm.identity"
                :entityType="DOMAIN_TERM_ENTITY_TYPE"
            />
            <Button
                v-if="domainTerm?.identity && isEditing"
                v-tooltip.bottom="{
                    value: 'Convert this domain term to a business entity.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-building"
                label="Convert to entity"
                :loading="isConverting"
                :disabled="isConverting"
                @click="confirmConvertToBusinessEntity"
            />
        </template>
        <Section editable>
            <TextAppArtefact
                v-if="domainTerm?.identity && description"
                v-model="description"
                :entityId="domainTerm.identity"
                :entityType="DOMAIN_TERM_ENTITY_TYPE"
                :isEditing="isEditing"
                :stekzModel="domainTerm"
                ref="textAppArtefact"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import EditableTitle from "@/components/EditableTitle.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { useDomainTermManager } from "@/composables/domain-term-manager";
import { useEditManager } from "@/composables/edit-manager";
import { api } from "@/lib/openapi";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";

const DOMAIN_TERM_ENTITY_TYPE = EntityType.DOMAIN_TERM;

const { isEditing } = useEditManager();
const { domainTerm, isFetching, description } = useDomainTermManager();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();
const route = useRoute();

const displaySpinner = ref(false);
const isConverting = ref(false);
watch(isFetching, (v) => (displaySpinner.value = v), { immediate: true });

const confirmConvertToBusinessEntity = (event: Event) => {
    if (!domainTerm.value?.identity) return;
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Convert this domain term to a business entity?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: "Cancel", severity: "secondary", outlined: true },
        acceptProps: { label: "Convert" },
        accept: convertToBusinessEntity,
    });
};

const convertToBusinessEntity = async () => {
    if (!domainTerm.value?.identity || isConverting.value) return;
    isConverting.value = true;
    try {
        const { response, data } =
            await api.startDomainDomainTermMgtConvertToBusinessEntityStartPost({
                body: { domain_term_id: domainTerm.value.identity },
            });
        if (!response.ok || !data) {
            toast.add({
                severity: "error",
                summary: `Error ${response.status}`,
                detail:
                    (data as { detail?: { msg?: string }[] })?.detail?.[0]
                        ?.msg ?? "Could not convert domain term.",
                life: 5000,
            });
            return;
        }
        await router.replace({
            name: "businessEntityPage",
            params: {
                orgName: route.params.orgName,
                stekName: route.params.stekName,
                businessEntityId: data.identity,
            },
        });
    } finally {
        isConverting.value = false;
    }
};
</script>
